import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, TrendingUp, DollarSign, Calendar, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';

const COLORS = ['#10b981', '#3b82f6', '#ef4444'];

export default function DischargeAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    // Set default start date to 30 days ago
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setDateRange(prev => ({
      ...prev,
      start_date: thirtyDaysAgo.toISOString().split('T')[0]
    }));
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start_date) params.append('start_date', dateRange.start_date);
      if (dateRange.end_date) params.append('end_date', dateRange.end_date);
      
      const response = await api.get(`/discharge-summary/analytics/bulk-report?${params.toString()}`);
      setAnalytics(response.data);
      toast.success('Analytics loaded successfully');
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange.start_date && dateRange.end_date) {
      fetchAnalytics();
    }
  }, [dateRange]);

  const handleExportCSV = () => {
    if (!analytics || !analytics.discharges) return;

    const headers = ['Patient Name', 'Patient Number', 'Diagnosis', 'Discharge Date', 'Condition', 'Total Billed', 'Amount Paid', 'Balance'];
    const rows = analytics.discharges.map(discharge => [
      discharge.patient?.full_name || 'N/A',
      discharge.patient?.patient_number || 'N/A',
      discharge.diagnosis_at_discharge,
      new Date(discharge.discharge_date).toLocaleDateString(),
      discharge.condition_at_discharge,
      discharge.billing?.total_amount?.toFixed(2) || '0.00',
      discharge.billing?.paid_amount?.toFixed(2) || '0.00',
      discharge.billing?.balance?.toFixed(2) || '0.00'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `discharge_analytics_${dateRange.start_date}_to_${dateRange.end_date}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported successfully');
  };

  if (loading && !analytics) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  const conditionData = analytics && analytics.conditions ? [
    { name: 'Improved', value: analytics.conditions.improved || 0, color: COLORS[0] },
    { name: 'Stable', value: analytics.conditions.stable || 0, color: COLORS[1] },
    { name: 'Critical', value: analytics.conditions.critical || 0, color: COLORS[2] }
  ].filter(item => item.value > 0) : [];

  return (
    <div data-testid="discharge-analytics-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
            Discharge Analytics
          </h1>
          <p className="text-slate-600 mt-2">Comprehensive discharge reports and financial analytics</p>
        </div>

        {/* Date Range Filter */}
        <Card className="border border-slate-200 shadow-sm mb-6">
          <CardContent className="p-6">
            <div className="flex items-end gap-4">
              <div className="flex-1">
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={dateRange.start_date}
                  onChange={(e) => setDateRange({...dateRange, start_date: e.target.value})}
                  data-testid="start-date-input"
                />
              </div>
              <div className="flex-1">
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={dateRange.end_date}
                  onChange={(e) => setDateRange({...dateRange, end_date: e.target.value})}
                  data-testid="end-date-input"
                />
              </div>
              <Button
                onClick={fetchAnalytics}
                disabled={loading}
                className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
                data-testid="refresh-analytics-button"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
              <Button
                onClick={handleExportCSV}
                variant="outline"
                disabled={!analytics || analytics.total_discharges === 0}
                data-testid="export-csv-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {analytics && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-1">Total Discharges</p>
                      <p className="text-3xl font-bold text-slate-900">{analytics.total_discharges}</p>
                    </div>
                    <div className="p-3 rounded-md bg-indigo-50">
                      <Users className="h-6 w-6 text-[#4A238F]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-1">Total Billed</p>
                      <p className="text-3xl font-bold text-slate-900">${analytics.financial_summary.total_billed.toFixed(0)}</p>
                    </div>
                    <div className="p-3 rounded-md bg-teal-50">
                      <DollarSign className="h-6 w-6 text-[#0f766e]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-1">Collection Rate</p>
                      <p className="text-3xl font-bold text-slate-900">{analytics.financial_summary.collection_rate.toFixed(1)}%</p>
                    </div>
                    <div className="p-3 rounded-md bg-emerald-50">
                      <TrendingUp className="h-6 w-6 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 mb-1">Outstanding</p>
                      <p className="text-3xl font-bold text-rose-600">${analytics.financial_summary.total_outstanding.toFixed(0)}</p>
                    </div>
                    <div className="p-3 rounded-md bg-rose-50">
                      <FileText className="h-6 w-6 text-rose-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Condition Breakdown Pie Chart */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Discharge Condition Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {conditionData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={conditionData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {conditionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-500 py-8">No data available</p>
                  )}
                </CardContent>
              </Card>

              {/* Financial Breakdown */}
              <Card className="border border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                    Financial Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={[
                        { name: 'Billed', amount: analytics.financial_summary.total_billed },
                        { name: 'Collected', amount: analytics.financial_summary.total_paid },
                        { name: 'Outstanding', amount: analytics.financial_summary.total_outstanding }
                      ]}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" stroke="#64748b" />
                      <YAxis stroke="#64748b" />
                      <Tooltip
                        formatter={(value) => `$${value.toFixed(2)}`}
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar dataKey="amount" fill="#0f766e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Discharge List */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Discharge Details ({analytics.total_discharges} records)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Patient</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Diagnosis</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Date</th>
                        <th className="text-left p-3 text-sm font-semibold text-slate-700">Condition</th>
                        <th className="text-right p-3 text-sm font-semibold text-slate-700">Billed</th>
                        <th className="text-right p-3 text-sm font-semibold text-slate-700">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.discharges.map((discharge) => (
                        <tr key={discharge.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3">
                            <div>
                              <p className="font-medium text-slate-900">{discharge.patient?.full_name}</p>
                              <p className="text-sm text-slate-600">{discharge.patient?.patient_number}</p>
                            </div>
                          </td>
                          <td className="p-3 text-sm text-slate-700">{discharge.diagnosis_at_discharge.substring(0, 40)}...</td>
                          <td className="p-3 text-sm text-slate-600">{new Date(discharge.discharge_date).toLocaleDateString()}</td>
                          <td className="p-3">
                            <span className={`inline-block text-xs px-2 py-1 rounded ${
                              discharge.condition_at_discharge === 'improved' ? 'bg-emerald-100 text-emerald-700' :
                              discharge.condition_at_discharge === 'stable' ? 'bg-purple-200 text-blue-700' :
                              'bg-rose-100 text-rose-700'
                            }`}>
                              {discharge.condition_at_discharge}
                            </span>
                          </td>
                          <td className="p-3 text-right text-sm font-medium text-slate-900">
                            ${discharge.billing?.total_amount?.toFixed(2) || '0.00'}
                          </td>
                          <td className="p-3 text-right text-sm font-medium text-rose-600">
                            ${discharge.billing?.balance?.toFixed(2) || '0.00'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {!analytics && !loading && (
          <Card className="border border-slate-200">
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Select a date range to view analytics</p>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  );
}
